import React, { useState, useEffect, useRef } from 'react';
import { 
    PaperAirplaneIcon, 
    FaceSmileIcon, 
    PhotoIcon, 
    UserCircleIcon, 
    MagnifyingGlassIcon,
    CheckBadgeIcon 
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { db, auth } from '../../firebase'; // Link with Al-Baron Firebase
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, where } from 'firebase/firestore';
// ✅ استيراد الدالة البارونية للإرسال المركزي
import { sendNotification } from '../../firebase'; 

const TeacherChat = () => {
    const [chats, setChats] = useState([]); 
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const scrollRef = useRef(null);
    const audioRef = useRef(new Audio('/notification.mp3')); // ملف الصوت الخاص بالتنبيه

    // 🔔 [AL-BARON_GLOBAL_LISTENER]: مراقبة كولكشن الإشعارات العام لتنبيه المدرس صوتياً فورياً
    useEffect(() => {
        // مراقبة الإشعارات غير المقروءة الخاصة بالمدرس
        // [AL-BARON_ULTRA_FILTER]
const q = query(
    collection(db, "notifications"), 
    where("role", "==", "teacher"),
    where("targetId", "==", "mOKBJYKFVSTZqA4amknV9Z3S08L2"), // ✅ لازم تحط هنا نفس الـ UID اللي ضفته بملف الطالب
    where("isRead", "==", false)
);
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    // 🛡️ ذكاء البارون: تشغيل الصوت للمدرس عند وصول أي إشعار جات جديد من أي طالب
                    audioRef.current.play().catch(e => console.log("Audio play deferred"));
                    
                    // إظهار تنبيه نظام (Desktop Notification) للمدرس لضمان الرد السريع
                    if ("Notification" in window && Notification.permission === "granted") {
                        new Notification("أكاديمية النور", {
                            body: "💬 أستاذي، لديك رسالة جديدة من طالب",
                            icon: "/logo192.png"
                        });
                    }
                }
            });
        });
        return () => unsubscribe();
    }, []);

    // 1. [SYNC_STUDENT_LIST] - جلب قائمة الطلاب الحقيقيين الذين أرسلوا رسائل
    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, "chats"), (snapshot) => {
            const studentChats = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setChats(studentChats);
            if (!activeChat && studentChats.length > 0) setActiveChat(studentChats[0]);
        });
        return unsubscribe;
    }, [activeChat]);

    // 2. [SYNC_MESSAGES_&_ALERTS] - جلب رسائل الطالب المختار حالياً مع نظام التنبيه الذكي
    useEffect(() => {
        if (!activeChat) return;
        const q = query(
            collection(db, "chats", activeChat.id, "messages"),
            orderBy("createdAt", "asc")
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // 🧠 [SMART_ALERT_LOGIC]: تشغيل الصوت للمدرس فقط إذا كانت الرسالة القادمة من الطالب داخل المحادثة النشطة
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    const msgData = change.doc.data();
                    // ذكاء البارون: إذا كان المرسل طالب، نشغل التنبيه الصوتي
                    if (msgData.senderRole !== 'teacher') {
                        audioRef.current.play().catch(e => console.log("Audio play deferred"));
                    }
                }
            });

            setMessages(fetchedMessages);
        });
        return unsubscribe;
    }, [activeChat]);

    useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    // 🚀 [FUNCTION: SEND_MESSAGE] - دالة الإرسال مع تنظيف الصندوق الفوري
    const handleSend = async (e) => {
        if (e) e.preventDefault();
        const messageToSend = newMessage.trim();
        
        if (!messageToSend || !activeChat) return;

        // ✅ [AL-BARON_FIX]: تنظيف الصندوق فوراً (Instant UI Feedback) لراحة الأستاذ
        setNewMessage(""); 

        const senderName = "الأستاذة نور عماد";
        const msgData = {
            text: messageToSend,
            senderRole: "teacher",
            senderName: senderName,
            createdAt: serverTimestamp(),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        try {
            // أ. إرسال الرسالة في كولكشن المحادثة الفرعي
            await addDoc(collection(db, "chats", activeChat.id, "messages"), msgData);

            // ✅ ب. إضافة التنبيه لجرس الطالب (Al-Baron Notification) باستخدام الدالة البارونية
            // ✅ [AL-BARON_FIX_UPDATED]: توجيه الإشعار للطالب المقصود فقط
await sendNotification(
    `رسالة جديدة من ${senderName}: ${messageToSend.substring(0, 20)}...`,
    'student',
    activeChat.id, // ✅ استبدلنا معرف الأستاذ بمعرف الطالب لضمان وصول التنبيه له وحده
    'chat'
);

        } catch (err) { 
            console.error("Error sending message:", err);
            // في حال فشل الإرسال حصراً، نعيد النص للصندوق حتى لا يضيع تعب المدرس
            setNewMessage(messageToSend); 
        }
    };

    return (
        <div className="h-[calc(100vh-140px)] flex gap-4 animate-in fade-in duration-700 overflow-hidden text-right px-2" dir="rtl" style={{ fontFamily: "'Cairo', sans-serif" }}>
            
            {/* 📱 [SIDEBAR: STUDENT_LIST] - القائمة الجانبية للطلاب بنظام الزجاج */}
            <div className="w-80 flex flex-col gap-4 hidden md:flex">
                <div className="ios-glass p-4 rounded-[35px] border border-white/30 dark:border-white/10 bg-white/20 dark:bg-white/5 backdrop-blur-3xl shadow-xl flex flex-col h-full overflow-hidden">
                    <h2 className="text-xl font-black text-gray-900 dark:text-white mb-4 px-2 tracking-tight">الرسائل الواردة</h2>
                    <div className="flex-1 overflow-y-auto custom-scroll space-y-2">
                        {chats.map((chat) => (
                            <div 
                                key={chat.id}
                                onClick={() => setActiveChat(chat)}
                                className={`p-4 rounded-2xl cursor-pointer transition-all flex items-center gap-3 border ${activeChat?.id === chat.id ? 'bg-[#007AFF] text-white shadow-lg shadow-blue-200' : 'hover:bg-white/40 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 border-transparent'}`}
                            >
                                <div className="w-10 h-10 bg-gray-100 dark:bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                                    <UserCircleIcon className="w-8 h-8 opacity-50" />
                                </div>
                                <div className="flex-1 overflow-hidden text-right">
                                    <h4 className="font-black text-[12px] truncate">{chat.studentName || "طالب النور"}</h4>
                                    <p className="text-[10px] truncate opacity-70">اضغط لعرض المحادثة</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 🛡️ [MAIN_CHAT_INTERFACE] - واجهة المحادثة الرئيسية المحصنة */}
            <div className="flex-1 flex flex-col min-w-0 h-full">
                <div className="ios-glass flex-1 flex flex-col rounded-[35px] border border-white/30 dark:border-white/10 bg-white/20 dark:bg-[#1C1C1E]/60 backdrop-blur-3xl shadow-2xl overflow-hidden">
                    {activeChat ? (
                        <>
                            <div className="p-5 bg-white/30 dark:bg-white/5 border-b border-white/20 dark:border-white/10 flex items-center justify-between z-10">
                                <h3 className="font-black text-gray-900 dark:text-white text-base">المحادثة مع: {activeChat.studentName || "الطالب"}</h3>
                                <div className="flex items-center gap-1.5 text-[#007AFF] bg-[#007AFF]/10 px-3 py-1 rounded-full border border-[#007AFF]/20">
                                    <CheckBadgeIcon className="w-4 h-4" />
                                    <span className="text-[9px] font-black uppercase tracking-wider">Verified Student</span>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto custom-scroll p-6 space-y-4 dark:bg-black/10">
                                {messages.map((msg) => (
                                    <div key={msg.id} className={`flex ${msg.senderRole === 'teacher' ? 'justify-start' : 'justify-end'}`}>
                                        <div className={`max-w-[75%] p-4 rounded-[22px] text-sm font-bold shadow-sm border ${
                                            msg.senderRole === 'teacher' 
                                            ? 'bg-[#007AFF] text-white rounded-br-none border-white/20' 
                                            : 'bg-white/70 dark:bg-white/10 text-gray-800 dark:text-gray-200 rounded-bl-none border-white/40 dark:border-white/5'
                                        }`}>
                                            {msg.text}
                                            <p className="text-[8px] mt-1 opacity-50 text-left">{msg.time}</p>
                                        </div>
                                    </div>
                                ))}
                                <div ref={scrollRef} />
                            </div>

                            <div className="p-5 bg-white/40 dark:bg-white/5 border-t border-white/20">
                                <form onSubmit={handleSend} className="flex items-center gap-3 bg-white/60 dark:bg-black/40 p-2 px-3 rounded-[28px] border border-white/40 shadow-inner">
                                    <input 
                                        type="text" 
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="اكتب ردك التعليمي هنا..." 
                                        className="flex-1 bg-transparent border-none py-3 px-2 outline-none font-bold text-sm text-gray-800 dark:text-white text-right" 
                                    />
                                    <button type="submit" className="w-11 h-11 bg-[#007AFF] text-white rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all">
                                        <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
                                    </button>
                                </form>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-400 font-bold">يرجى اختيار طالب من القائمة الجانبية للرد عليه</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeacherChat;